import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  AppBar,
  Toolbar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Paper,
  IconButton,
} from "@mui/material";
import { QrCode2, ArrowBack, Logout } from "@mui/icons-material";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeCanvas } from 'qrcode.react';
import BrandedQRCode from './BrandedQRCode.jsx';

const QRDetailPage = () => {
  const { packageId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState('');
  const themeInstance = useTheme();
  const isMobile = useMediaQuery(themeInstance.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchPackageDetails();
  }, [packageId]);

  const fetchPackageDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const uuid = location.pathname === '/hindalco' 
        ? new URLSearchParams(location.search).get('uuid')
        : packageId;

      if (!uuid) {
        throw new Error('No package ID provided');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/get?uuid=${uuid}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch package details');
      }

      const data = await response.json();
      setPackageData(data);
      
      // Generate QR code URL
      const baseUrl = window.location.origin;
      const qrUrl = `${baseUrl}/hindalco?uuid=${uuid}`;
      setQrData(qrUrl);
    } catch (error) {
      console.error('Error fetching package details:', error);
      setError('Failed to load package details');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadImage = async () => {
    const card = document.getElementById('consignment-card-content');
    console.log("Card element for image download:", card); // Debug log
    if (card) {
      window.scrollTo(0, 0); // Scroll to top before capturing
      
      // Add a small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        const canvas = await html2canvas(card, {
          scale: 3, // Higher scale for crisp QR codes
          useCORS: true, // Enable cross-origin images
          allowTaint: true, // Allow tainted canvases for better compatibility
          backgroundColor: '#ffffff', // Ensure white background
          logging: false, // Disable logging for cleaner output
          height: card.offsetHeight,
          width: card.offsetWidth,
          scrollX: 0,
          scrollY: 0,
          pixelRatio: window.devicePixelRatio || 1 // Use device pixel ratio for better quality
        });
        
        console.log("Canvas dimensions:", canvas.width, "x", canvas.height); // Debug log
        
        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error("Canvas has zero dimensions");
        }
        
        // Use maximum quality for PNG export
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        if (!imgData || imgData === 'data:,') {
          throw new Error("Failed to generate image data");
        }
        
        const link = document.createElement('a');
        link.href = imgData;
        const fileName = `Consignment_Card_${packageData?.package_number || 'Package'}_${new Date().toISOString().split('T')[0]}.png`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error during image download:", error);
        alert("Failed to download image. Please try again.");
      }
    } else {
      console.error("Consignment card element not found for image download.");
    }
  };

  const handleDownloadPDF = async () => {
    const card = document.getElementById('consignment-card-content');
    console.log("Card element for PDF download:", card); // Debug log
    if (card) {
      window.scrollTo(0, 0); // Scroll to top before capturing
      
      // Add a small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        const canvas = await html2canvas(card, {
          scale: 3, // Match the image download scale
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          height: card.offsetHeight,
          width: card.offsetWidth,
          scrollX: 0,
          scrollY: 0,
          pixelRatio: window.devicePixelRatio || 1
        });
        
        console.log("Canvas dimensions for PDF:", canvas.width, "x", canvas.height); // Debug log
        
        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error("Canvas has zero dimensions");
        }
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        if (!imgData || imgData === 'data:,') {
          throw new Error("Failed to generate image data for PDF");
        }

        // Calculate PDF dimensions to fit the content properly
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        // Create PDF with appropriate page size
        const pdf = new jsPDF({
          orientation: imgWidth > imgHeight ? 'landscape' : 'portrait',
          unit: 'pt',
          format: 'a4'
        });
        
        // Get PDF page dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        // Calculate scaling to fit the image on the page
        const widthRatio = pageWidth / imgWidth;
        const heightRatio = pageHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;
        
        // Center the image on the page
        const x = (pageWidth - scaledWidth) / 2;
        const y = (pageHeight - scaledHeight) / 2;

        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
        const fileName = `Consignment_Card_${packageData?.package_number || 'Package'}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error("Error during PDF download:", error);
        alert("Failed to download PDF. Please try again.");
      }
    } else {
      console.error("Consignment card element not found for PDF download.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress size={60} sx={{ color: '#1F425C' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
              <AppBar position="static" color="primary" elevation={0} sx={{ bgcolor: '#1F425C' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2, color: '#fff' }}
          >
            <ArrowBack />
          </IconButton>
          <img src="/HINDALCO.NS_BIG.png" alt="Hindalco" style={{ marginRight: '16px', height: '32px', width: 'auto' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff', fontWeight: 500 }}>
            Hindalco QR Management
          </Typography>
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              ml: 2,
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 12, pb: 4 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Package Details
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Here are the details for your package
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List>
                <ListItem>
                  <ListItemText
                    primary="Package Number"
                    secondary={packageData?.package_number}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Vendor Name"
                    secondary={packageData?.vendor_name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Location"
                    secondary={packageData?.location || 'Not specified'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Owner"
                    secondary={packageData?.owner || 'Not specified'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Delivery Date"
                    secondary={new Date(packageData?.delivery_date).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created By"
                    secondary={packageData?.qr_created_by}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created At"
                    secondary={new Date(packageData?.qr_created_at).toLocaleString()}
                  />
                </ListItem>
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: '#1F425C', fontWeight: 600 }}>
                Items
              </Typography>
              <List>
                {packageData?.items?.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={`${index + 1}. ${item}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          
          {/* Information Card for Download */}
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                mt: { xs: 4, md: 0 }, // Add top margin on small screens if not next to other card
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Consignment Information Card
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Use this card for easy scanning and record keeping
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box id="consignment-card-content" sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Package Details:</Typography>
                <Typography variant="body2"><strong>Package No.:</strong> {packageData?.package_number}</Typography>
                <Typography variant="body2"><strong>Vendor:</strong> {packageData?.vendor_name}</Typography>
                <Typography variant="body2"><strong>Location:</strong> {packageData?.location || 'Not specified'}</Typography>
                <Typography variant="body2"><strong>Owner:</strong> {packageData?.owner || 'Not specified'}</Typography>
                <Typography variant="body2"><strong>Delivery Date:</strong> {new Date(packageData?.delivery_date).toLocaleDateString()}</Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Items:</Typography>
                  <List dense>
                    {packageData?.items?.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={`${index + 1}. ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: { xs: 2, md: 0 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>QR Code:</Typography>
                      {qrData && (
                        <Box sx={{
                          transform: 'scale(0.5)', // Scale down visually to 120px equivalent
                          transformOrigin: 'center',
                          '& canvas': {
                            imageRendering: 'pixelated', // Ensure crisp rendering
                            imageRendering: '-moz-crisp-edges',
                            imageRendering: 'crisp-edges'
                          }
                        }}>
                          <BrandedQRCode
                            value={qrData || ''}
                            size={240}
                            title="Hindalco"
                            subtitle="Consignment Details"
                            variant="compact"
                            logoUrl="/HINDALCO.NS_BIG.png"
                            logoSize={0.38}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 4, position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#AC1E15',
                    '&:hover': {
                      bgcolor: '#8B1612',
                    },
                  }}
                  onClick={handleDownloadImage}
                >
                  Download as Image
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#1F425C',
                    color: '#1F425C',
                    '&:hover': {
                      borderColor: '#1F2F6B',
                      bgcolor: 'rgba(38, 58, 127, 0.05)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'transform 0.2s ease-in-out',
                  }}
                  onClick={handleDownloadPDF}
                >
                  Download as PDF
                </Button>
                </Box>
                
                {/* Small QR Code in bottom right
                {qrData && (
                  <Box sx={{ 
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    p: 1,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <QRCodeCanvas
                      value={qrData || ''}
                      size={60}
                      level="M"
                      includeMargin={true}
                    />
                  </Box>
                )} */}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>


    </Box>
  );
};

export default QRDetailPage;
